/**
 * 기술스펙 8.0/8.1: roles는 리스팅당 최대 3개(어뷰징 방지), 선택한 role마다 rich_context에
 * 전용 필드 그룹이 추가된다. 이 파일이 role 값·필드 스키마(키·필수 여부)의 단일 정의 지점이며,
 * 프론트(폼 렌더링)와 백엔드(퍼블리시 시 필수 필드 검증)가 동일한 정의를 가져다 쓴다.
 *
 * 사람이 읽는 라벨 텍스트는 여기 두지 않고 messages/{locale}.json의 roles/roleFields 네임스페이스에
 * 둔다 — 글로벌 서비스라 한국어를 하드코딩하면 안 되고, API 응답(missing_role_fields)도 role/key만
 * 반환해 클라이언트가 사용자 로케일로 번역해서 보여준다.
 */

export const ROLE_VALUES = [
  "seeking_investment",
  "providing_investment",
  "providing_service",
  "seeking_suppliers",
  "seeking_collaboration",
  "seeking_customers",
] as const;

export type Role = (typeof ROLE_VALUES)[number];

export const MAX_ROLES = 3;

export function isValidRole(value: string): value is Role {
  return (ROLE_VALUES as readonly string[]).includes(value);
}

export interface RoleField {
  key: string;
  required: boolean;
}

/**
 * 업종(category_id)은 모든 role에 공통으로 필요한 "업종/서비스 업종" 요건을 이미 충족하므로
 * role별 필드 목록에서는 중복 정의하지 않는다(8.1 표의 "업종" 계열 필드는 공통 category_id로 흡수).
 */
export const ROLE_FIELDS: Record<Role, RoleField[]> = {
  seeking_investment: [
    { key: "growth_stage", required: true },
    { key: "team_size_range", required: false },
  ],
  providing_investment: [
    { key: "investment_stage", required: true },
    { key: "sectors", required: true },
    { key: "ticket_size_range", required: false },
    { key: "screening_process", required: false },
  ],
  providing_service: [
    { key: "price_range", required: false },
    { key: "sla", required: false },
    { key: "certifications", required: false },
  ],
  seeking_suppliers: [
    { key: "sourcing_category", required: true },
    { key: "expected_order_volume", required: false },
  ],
  seeking_collaboration: [],
  seeking_customers: [
    { key: "target_customer_segment", required: true },
    { key: "sales_materials_url", required: false },
  ],
};

export interface MissingFieldReport {
  role: Role;
  key: string;
}

/**
 * 퍼블리시 시점에만 role별 필수 필드를 강제한다(등록 시점엔 초안이 불완전해도 저장 가능).
 * rich_context는 role별로 네임스페이스를 분리해 저장한다: { [role]: { [fieldKey]: value } }.
 */
export function findMissingRequiredFields(
  roles: string[],
  richContext: Record<string, Record<string, unknown>>
): MissingFieldReport[] {
  const missing: MissingFieldReport[] = [];
  for (const role of roles) {
    if (!isValidRole(role)) continue;
    const fields = ROLE_FIELDS[role];
    const values = richContext[role] ?? {};
    for (const field of fields) {
      if (!field.required) continue;
      const value = values[field.key];
      const isEmpty = value === undefined || value === null || (typeof value === "string" && value.trim() === "");
      if (isEmpty) missing.push({ role, key: field.key });
    }
  }
  return missing;
}
